/**
 * TBMuktBharat Admin Submissions Page
 * Review and manage competition submissions
 */

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Trophy, FileText, CheckCircle, XCircle, Clock, Eye, Award,
  Calendar, MapPin, Building, Users, ArrowLeft, Loader2,
  FileSpreadsheet, Filter, Search, Star, MessageSquare
} from 'lucide-react'
import competitionService, { Competition, CompetitionSubmission, SubmissionStatus } from '@/services/competitionService'
import { Card, Badge, EmptyState, Button } from '@/components/ui/premium-components'
import { StatsCard, InputField, Select } from '@/components/ui/data-display'
import GovLayout from '@/components/layout/GovLayout'

// ================================================================================
// TYPE ICON COMPONENT
// ================================================================================
const TypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const icons: Record<string, string> = {
    POSTER: '🎨',
    QUIZ: '🧠',
    ESSAY: '📝',
    DRAWING: '🖌️',
    SLOGAN: '💬',
    DEBATE: '🎤',
    PHOTOGRAPHY: '📷',
  }
  return <span className="text-2xl">{icons[type] || '🏆'}</span>
}

// ================================================================================
// STATUS BADGE COMPONENT
// ================================================================================
const StatusBadge: React.FC<{ status: SubmissionStatus }> = ({ status }) => {
  const config = {
    PENDING: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
    APPROVED: { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-300' },
    REJECTED: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-300' },
  }
  const { label, color } = config[status]
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${color}`}>
      {status === 'PENDING' && <Clock className="w-3 h-3" />}
      {status === 'APPROVED' && <CheckCircle className="w-3 h-3" />}
      {status === 'REJECTED' && <XCircle className="w-3 h-3" />}
      {label}
    </span>
  )
}

// ================================================================================
// REVIEW MODAL COMPONENT
// ================================================================================
interface ReviewModalProps {
  submission: CompetitionSubmission | null
  isOpen: boolean
  onClose: () => void
  onReview: (submissionId: number, status: SubmissionStatus, feedback: string, rank?: number) => Promise<void>
  onGenerateCertificate: (submissionId: number) => Promise<string | null>
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  submission,
  isOpen,
  onClose,
  onReview,
  onGenerateCertificate,
}) => {
  const [status, setStatus] = useState<SubmissionStatus>('APPROVED')
  const [feedback, setFeedback] = useState('')
  const [rank, setRank] = useState<number | undefined>()
  const [loading, setLoading] = useState(false)
  const [certificateLoading, setCertificateLoading] = useState(false)
  const [certificateUrl, setCertificateUrl] = useState<string | null>(null)

  useEffect(() => {
    if (submission) {
      setStatus(submission.status || 'APPROVED')
      setFeedback(submission.feedback || '')
      setRank(submission.rank)
      setCertificateUrl(submission.certificateUrl || null)
    }
  }, [submission])

  const handleSubmit = async () => {
    if (!submission) return
    setLoading(true)
    try {
      await onReview(submission.id, status, feedback, rank)
      onClose()
    } catch (error) {
      console.error('Review failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateCertificate = async () => {
    if (!submission) return
    setCertificateLoading(true)
    try {
      const url = await onGenerateCertificate(submission.id)
      if (url) {
        setCertificateUrl(url)
      }
    } catch (error) {
      console.error('Certificate generation failed:', error)
    } finally {
      setCertificateLoading(false)
    }
  }

  if (!isOpen || !submission) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-neutral-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-white">
              Review Submission
            </h2>
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Submission Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-neutral-500">Participant</label>
              <p className="font-medium text-neutral-900 dark:text-white">{submission.fullName}</p>
            </div>
            <div>
              <label className="text-sm text-neutral-500">Email</label>
              <p className="text-neutral-700 dark:text-neutral-300">{submission.email || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-neutral-500">School/College</label>
              <p className="text-neutral-700 dark:text-neutral-300">{submission.schoolCollegeName || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-neutral-500">Class/Standard</label>
              <p className="text-neutral-700 dark:text-neutral-300">{submission.classStandard || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-neutral-500">District</label>
              <p className="text-neutral-700 dark:text-neutral-300">{submission.district || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-neutral-500">State</label>
              <p className="text-neutral-700 dark:text-neutral-300">{submission.state || 'N/A'}</p>
            </div>
          </div>

          {/* Submission Title & Description */}
          {submission.submissionTitle && (
            <div>
              <label className="text-sm text-neutral-500">Submission Title</label>
              <p className="font-medium text-neutral-900 dark:text-white">{submission.submissionTitle}</p>
            </div>
          )}
          {submission.submissionDescription && (
            <div>
              <label className="text-sm text-neutral-500">Description</label>
              <p className="text-neutral-700 dark:text-neutral-300">{submission.submissionDescription}</p>
            </div>
          )}

          {/* File Preview */}
          {submission.fileUrl && (
            <div>
              <label className="text-sm text-neutral-500">Submitted File</label>
              <div className="mt-2 flex items-center gap-3">
                <a
                  href={submission.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                >
                  <Eye className="w-4 h-4" />
                  View File
                </a>
                <span className="text-sm text-neutral-500">
                  {submission.fileType} • {submission.fileSize ? `${(submission.fileSize / 1024).toFixed(1)} KB` : 'N/A'}
                </span>
              </div>
            </div>
          )}

          {/* Review Form */}
          <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-800">
            <h3 className="font-semibold text-neutral-900 dark:text-white">Review Decision</h3>

            <div>
              <label className="text-sm text-neutral-500 mb-2 block">Status</label>
              <Select
                options={[
                  { value: 'APPROVED', label: '✅ Approve' },
                  { value: 'REJECTED', label: '❌ Reject' },
                  { value: 'PENDING', label: '⏳ Keep Pending' },
                ]}
                value={status}
                onChange={(val: string) => setStatus(val as SubmissionStatus)}
                className="w-full"
              />
            </div>

            {status === 'APPROVED' && (
              <div>
                <label className="text-sm text-neutral-500 mb-2 block">Rank (Optional)</label>
                <InputField
                  type="number"
                  placeholder="Enter rank position"
                  value={rank?.toString() || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRank(e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
            )}

            <div>
              <label className="text-sm text-neutral-500 mb-2 block">Feedback</label>
              <textarea
                className="w-full p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white"
                rows={3}
                placeholder="Provide feedback for the participant..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
            </div>

            {/* Certificate */}
            {status === 'APPROVED' && (
              <div className="space-y-3">
                <label className="text-sm text-neutral-500 block">Certificate</label>
                {certificateUrl ? (
                  <div className="flex items-center gap-3">
                    <a
                      href={certificateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                    >
                      <Award className="w-4 h-4" />
                      View Certificate
                    </a>
                    <span className="text-sm text-green-600">Certificate generated</span>
                  </div>
                ) : (
                  <button
                    onClick={handleGenerateCertificate}
                    disabled={certificateLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 disabled:opacity-50"
                  >
                    {certificateLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Award className="w-4 h-4" />
                    )}
                    Generate Certificate
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-neutral-200 dark:border-neutral-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Review'
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  )
}

// ================================================================================
// MAIN COMPONENT
// ================================================================================

const AdminSubmissionsPage: React.FC = () => {
  const { competitionId } = useParams<{ competitionId: string }>()
  const navigate = useNavigate()

  const [competition, setCompetition] = useState<Competition | null>(null)
  const [submissions, setSubmissions] = useState<CompetitionSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<SubmissionStatus | ''>('')
  const [selectedSubmission, setSelectedSubmission] = useState<CompetitionSubmission | null>(null)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  })

  useEffect(() => {
    if (competitionId) {
      fetchCompetition()
      fetchSubmissions()
    }
  }, [competitionId])

  const fetchCompetition = async () => {
    try {
      const data = await competitionService.getCompetitionById(Number(competitionId))
      setCompetition(data)
    } catch (err) {
      console.error('Failed to fetch competition:', err)
    }
  }

  const fetchSubmissions = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await competitionService.getCompetitionSubmissions(
        Number(competitionId),
        { status: filterStatus || undefined, page: 0, size: 100 }
      )

      let filtered = response.content
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        filtered = filtered.filter(
          s =>
            s.fullName.toLowerCase().includes(query) ||
            s.schoolCollegeName?.toLowerCase().includes(query) ||
            s.district?.toLowerCase().includes(query) ||
            s.state?.toLowerCase().includes(query)
        )
      }

      setSubmissions(filtered)
      setStats({
        total: response.totalElements,
        pending: response.content.filter(s => s.status === 'PENDING').length,
        approved: response.content.filter(s => s.status === 'APPROVED').length,
        rejected: response.content.filter(s => s.status === 'REJECTED').length,
      })
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load submissions')
    } finally {
      setLoading(false)
    }
  }

  const handleReview = async (
    submissionId: number,
    status: SubmissionStatus,
    feedback: string,
    rank?: number
  ) => {
    try {
      await competitionService.reviewSubmission(submissionId, { status, feedback })
      if (status === 'APPROVED' && rank) {
        await competitionService.assignRank(submissionId, rank)
      }
      fetchSubmissions()
    } catch (err) {
      console.error('Review failed:', err)
      throw err
    }
  }

  const handleGenerateCertificate = async (submissionId: number): Promise<string | null> => {
    try {
      const result = await competitionService.generateCertificate(submissionId)
      return result.certificateUrl
    } catch (err) {
      console.error('Certificate generation failed:', err)
      return null
    }
  }

  const handleExportCsv = async () => {
    try {
      const blob = await competitionService.exportSubmissionsCsv(Number(competitionId))
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `competition-${competitionId}-submissions.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      a.remove()
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const openReviewModal = (submission: CompetitionSubmission) => {
    setSelectedSubmission(submission)
    setIsReviewModalOpen(true)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  return (
    <GovLayout>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => navigate('/admin/competitions')}
            className="flex items-center gap-1 text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Competitions
          </button>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-50">
              {competition && <TypeIcon type={competition.type} />}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                {competition?.title || 'Loading...'}
              </h1>
              <p className="text-neutral-500">
                Manage and review competition submissions
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            leftIcon={<FileSpreadsheet className="w-4 h-4" />}
            onClick={handleExportCsv}
          >
            Export CSV
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
      >
        <StatsCard
          icon={<Users className="w-5 h-5" />}
          label="Total Submissions"
          value={stats.total}
          iconBgColor="bg-blue-100"
        />
        <StatsCard
          icon={<Clock className="w-5 h-5" />}
          label="Pending Review"
          value={stats.pending}
          iconBgColor="bg-yellow-100"
        />
        <StatsCard
          icon={<CheckCircle className="w-5 h-5" />}
          label="Approved"
          value={stats.approved}
          iconBgColor="bg-green-100"
        />
        <StatsCard
          icon={<XCircle className="w-5 h-5" />}
          label="Rejected"
          value={stats.rejected}
          iconBgColor="bg-red-100"
        />
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search by name, school, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Select
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'PENDING', label: 'Pending' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
                value={filterStatus}
                onChange={(val: string) => setFilterStatus(val as SubmissionStatus | '')}
                placeholder="Status"
                className="w-40"
              />
              <Button variant="outline" onClick={fetchSubmissions}>
                Apply Filters
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Submissions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : error ? (
          <Card className="text-center py-12">
            <p className="text-red-500">{error}</p>
            <Button variant="primary" onClick={fetchSubmissions} className="mt-4">
              Try Again
            </Button>
          </Card>
        ) : submissions.length === 0 ? (
          <EmptyState
            icon={<FileText className="w-16 h-16 text-neutral-300" />}
            title="No Submissions Found"
            description="No submissions match your current filters"
          />
        ) : (
          <div className="space-y-4">
            {submissions.map((submission, index) => (
              <motion.div
                key={submission.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <Card hover="lift" className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Submission Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-neutral-900 dark:text-white">
                          {submission.fullName}
                        </h3>
                        <StatusBadge status={submission.status} />
                        {submission.rank && (
                          <Badge variant="warning" size="sm">
                            <Star className="w-3 h-3 mr-1" />
                            Rank #{submission.rank}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500">
                        {submission.schoolCollegeName && (
                          <span className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            {submission.schoolCollegeName}
                          </span>
                        )}
                        {submission.district && submission.state && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {submission.district}, {submission.state}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(submission.submittedAt || '')}
                        </span>
                      </div>
                      {submission.submissionTitle && (
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                          "{submission.submissionTitle}"
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {submission.fileUrl && (
                        <a
                          href={submission.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                          title="View File"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                      )}
                      <Button
                        variant={submission.status === 'PENDING' ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => openReviewModal(submission)}
                      >
                        {submission.status === 'PENDING' ? 'Review' : 'Edit'}
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Review Modal */}
      <ReviewModal
        submission={selectedSubmission}
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false)
          setSelectedSubmission(null)
        }}
        onReview={handleReview}
        onGenerateCertificate={handleGenerateCertificate}
      />
    </GovLayout>
  )
}

export default AdminSubmissionsPage